// 🔧 ORGANIZATION.JS - FIXES FOR:
// 1. Show success message when organization saves
// 2. Proper navigation after success
// 3. Clear navigation logic

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getOrganizationByUser,
  getOrganizationByOrgId, 
  saveOrganization, 
  getUser, 
  saveSubscriptionFromPlan,
  getSubscriptions 
} from '../services/api';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { Switch } from "./ui/switch";
import { Separator } from "./ui/separator";
import { Loader2, Building2, Bell, CheckCircle } from "lucide-react";
import { useSuperAdmin } from '../contexts/SuperAdminContext';

const TOP_UP_AMOUNTS = [
  { value: 50,  label: '£50'  },
  { value: 100, label: '£100' },
  { value: 200, label: '£200' },
  { value: 300, label: '£300' }
];

const Organization = () => {
  const [organization, setOrganization] = useState({
    id: '',
    name: '',
    email: '',
    logo: '',
    briefInformation: '',
    defaultTopUpAmount: 100,
    website: '',
    address: '',
    country: '',
    phoneNumber: '',
    mobileNumber: '',
    callSummaryEmailEnabled:    true,
    callSummarySmsEnabled: false,
    callSummaryEmail:           '',
    user: [],
  });

  const { organizationId, setNavigationEnabled } = useContext(OrganizationContext);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [success, setSuccess]               = useState(null);
  const [isSaving, setIsSaving]             = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [hasCheckedOut, setHasCheckedOut]   = useState(false);
  const navigate = useNavigate();
  const { isSuperAdmin } = useSuperAdmin();

  // ===============================================
  // 🔧 FIX: Load organization on mount
  // ===============================================
  useEffect(() => {
    const fetchOrganizationAndSubscription = async () => {
      try {
        let fetchedOrganization;

        if (isSuperAdmin && organizationId) {
          fetchedOrganization = await getOrganizationByOrgId(organizationId);
        } else {
          const email = getUser();
          fetchedOrganization = await getOrganizationByUser(email);
        }

        setOrganization({
          ...fetchedOrganization,
          name:                       fetchedOrganization.name                       ?? '',
          logo:                       fetchedOrganization.logo                       ?? '',
          briefInformation:           fetchedOrganization.briefInformation           ?? '',
          defaultTopUpAmount:         fetchedOrganization.defaultTopUpAmount         ?? 100,
          callSummaryEmailEnabled:    fetchedOrganization.callSummaryEmailEnabled    ?? true,
          callSummarySmsEnabled: fetchedOrganization.callSummarySmsEnabled ?? false,
          callSummaryEmail:           fetchedOrganization.callSummaryEmail           ?? '',
        });

        setNavigationEnabled(Boolean(fetchedOrganization?.id));

        if (fetchedOrganization?.id) {
          const subscriptions = await getSubscriptions(fetchedOrganization.id);
          setHasSubscription(subscriptions && subscriptions?.length > 0);
          setHasCheckedOut(subscriptions &&
            Array.isArray(subscriptions) &&
            subscriptions.length > 0 &&
            subscriptions.some(sub => sub.isActive && sub.stripeSubscriptionId != null));
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
        setError('Failed to fetch organization details');
        setNavigationEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationAndSubscription();
  }, [setNavigationEnabled, isSuperAdmin, organizationId]);

  const handleChange = (field, value) => {
    setOrganization(prev => ({ ...prev, [field]: value }));
    // Clear messages on change
    setError(null);
    setSuccess(null);
  };

  // ===============================================
  // 🔧 FIX: Proper save with success message
  // ===============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Saving organization:', organization);
      
      // 1. Save organization
      const orgSaved = await saveOrganization(organization);
      
      // Validate response
      if (!orgSaved || !orgSaved.id) {
        throw new Error('Organization save failed - no ID returned');
      }

      console.log('✅ Organization saved successfully:', orgSaved);
      
      // 2. Update local state with saved organization
      setOrganization(orgSaved);
      setNavigationEnabled(true);
      
      // 3. Show success message
      setSuccess('✅ Organization saved successfully! Redirecting to checkout...');

      // 4. Navigate after delay to let user see the success message
      setTimeout(() => {
        // Determine where to navigate
        const shouldNavigateToCheckout = !hasSubscription || !hasCheckedOut;
        
        if (shouldNavigateToCheckout) {
          // First time: go to pricing/checkout
          localStorage.setItem('organizationId', orgSaved.id);
          navigate('/pricing');
        } else {
          // Already has subscription: go to subscriptions
          navigate('/subscriptions');
        }
      }, 1500); // Give user 1.5 seconds to see success message

    } catch (error) {
      console.error('❌ Error saving organization:', error);
      setError(error.message || 'Failed to save organization. Please try again.');
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto py-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Organization Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* ✅ ERROR ALERT */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ✅ SUCCESS ALERT */}
          {success && (
            <Alert className="mb-6 bg-green-50 border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </div>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* ── Core Details ───────────────────────────────────── */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  value={organization.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="logo">Organization Logo URL</Label>
                <Input
                  id="logo"
                  value={organization.logo}
                  onChange={(e) => handleChange('logo', e.target.value)}
                  placeholder="Enter logo URL"
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="email">Organization Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={organization.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="briefInformation">Brief Information</Label>
                <Textarea
                  id="briefInformation"
                  value={organization.briefInformation}
                  onChange={(e) => handleChange('briefInformation', e.target.value)}
                  rows={4}
                  placeholder="Enter organization description"
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="defaultTopUpAmount">Default Top-up Amount</Label>
                <Select
                  value={organization.defaultTopUpAmount.toString()}
                  onValueChange={(value) => handleChange('defaultTopUpAmount', parseInt(value))}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent>
                    {TOP_UP_AMOUNTS.map((amount) => (
                      <SelectItem key={amount.value} value={amount.value.toString()}>
                        {amount.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={organization.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="Enter website URL"
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={organization.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  rows={3}
                  placeholder="Enter organization address"
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={organization.country || ''}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="Enter country"
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  value={organization.phoneNumber || ''}
                  onChange={(e) => handleChange('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                  disabled={isSaving}
                />
              </div>

              <div>
                <Label htmlFor="mobileNumber">Mobile Number</Label>
                <Input
                  id="mobileNumber"
                  value={organization.mobileNumber || ''}
                  onChange={(e) => handleChange('mobileNumber', e.target.value)}
                  placeholder="Enter mobile number"
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* ── Call Summary Notifications ─────────────────────── */}
            <Separator />

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-semibold text-base">Call Summary Notifications</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose how you'd like to be notified after each AI call completes.
              </p>

              {/* Email toggle */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label htmlFor="callSummaryEmailEnabled" className="font-medium">
                    Email Notification
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Receive a full call summary with transcript by email.
                  </p>
                </div>
                <Switch
                  id="callSummaryEmailEnabled"
                  checked={organization.callSummaryEmailEnabled}
                  onCheckedChange={(checked) => handleChange('callSummaryEmailEnabled', checked)}
                  disabled={isSaving}
                />
              </div>

              {/* Dedicated summary email — only shown when email is on */}
              {organization.callSummaryEmailEnabled && (
                <div>
                  <Label htmlFor="callSummaryEmail">
                    Summary Recipient Email{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="callSummaryEmail"
                    type="email"
                    value={organization.callSummaryEmail || ''}
                    onChange={(e) => handleChange('callSummaryEmail', e.target.value)}
                    placeholder={`Defaults to ${organization.email || 'organization email'}`}
                    disabled={isSaving}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave blank to use the organization email above.
                  </p>
                </div>
              )}

              {/* SMS toggle */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label htmlFor="callSummarySmsEnabled" className="font-medium">
                    SMS Notification
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Receive a brief call summary to your mobile number
                    {organization.mobileNumber
                      ? ` (${organization.mobileNumber})`
                      : ' — set a mobile number above first'}.
                  </p>
                </div>
                <Switch
                  id="callSummarySmsEnabled"
                  checked={organization.callSummarySmsEnabled}
                  onCheckedChange={(checked) => handleChange('callSummarySmsEnabled', checked)}
                  disabled={!organization.mobileNumber || isSaving}
                />
              </div>

              {/* Warn if SMS toggled on but no mobile */}
              {organization.callSummarySmsEnabled && !organization.mobileNumber && (
                <Alert variant="destructive">
                  <AlertDescription>
                    Please enter a mobile number above to enable SMS notifications.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* ✅ SUBMIT BUTTON WITH PROPER STATE */}
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving Organization...
                </>
              ) : (
                hasCheckedOut ? 'Save Organization' : 'Save Organization & Continue to Checkout'
              )}
            </Button>

          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Organization;